import { captureAttribution } from "@/tracking/attribution";
import { createEffect, createRoot } from "solid-js";
import { themeState, type TenantConfig } from "@/shared/stores/themeStore";
import { initTracking } from "./analytics";
import {
  setTrackingConfig,
  trackingConfig,
  type TrackingConfig,
  type TrackingEnvironment,
  type TrackingVendor,
} from "./config";

/**
 * Where the tracking layer is switched on, and the only place the server's tracking configuration
 * enters it.
 *
 * <p>The configuration rides on <c>GET /api/tenant/config</c>, which the theme store already loads
 * and which resolves the tenant from the request hostname server-side. It is read from there rather
 * than fetched again: a second request would resolve the same host a second time, and — worse —
 * would give the app two answers about which container it is allowed to load. A measurement id may
 * never come from anywhere a link author can reach, and the host-resolved tenant is the one
 * allowlist that qualifies.</p>
 *
 * <p>Nothing here decides whether anything is sent. This module hands the configuration over and
 * starts the consent subscription; consent, user group and the container itself are decided in
 * analytics.ts and gtm.ts, which is why a visitor who has agreed to nothing sees no network
 * activity at all as a result of calling <c>startTracking</c>.</p>
 */

// ── The tenant's tracking section ────────────────────────────────────────────

/**
 * The tracking object as it arrives on the tenant response. It is not part of the theme store's
 * <c>TenantConfig</c> interface — that type describes branding — but the store merges the server
 * answer over its defaults, so a field the interface does not name still survives into the store.
 * Reading it through this shape keeps the coupling explicit instead of casting at the call site.
 */
interface TenantTrackingSection {
  tracking?: unknown;
}

const ENVIRONMENTS: readonly TrackingEnvironment[] = ["development", "test", "production"];

const VENDORS: readonly TrackingVendor[] = ["ga4", "googleAds", "meta", "tiktok"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A string, or null. Blank counts as absent — an empty measurement id configures nothing. */
function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function textList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const items = value.filter((item): item is string => typeof item === "string" && item.trim() !== "");
  return items.length > 0 ? items : null;
}

function labelMap(value: unknown): Record<string, string> | null {
  if (!isRecord(value)) return null;
  const labels: Record<string, string> = {};
  for (const [key, label] of Object.entries(value)) {
    if (typeof label === "string" && label.trim() !== "") labels[key] = label;
  }
  return Object.keys(labels).length > 0 ? labels : null;
}

/**
 * Read the tenant's tracking section into the shape the tracking layer expects.
 *
 * <p>Every field is narrowed by type, and anything that does not narrow becomes null rather than
 * being passed along as-is. The reason is not tidiness: <c>allowedHostnames</c> is consulted with
 * <c>.some()</c>, and a string where a list was meant would throw inside the check that is supposed
 * to keep a staging build out of the production property. A configuration that cannot be read is a
 * configuration that sends nothing.</p>
 *
 * @returns the configuration, or null when the section is absent or is not an object — both of
 *   which turn everything off, since <c>setTrackingConfig</c> merges over <c>TRACKING_OFF</c>.
 */
export function readTrackingConfig(raw: unknown): TrackingConfig | null {
  if (!isRecord(raw)) return null;

  const environment = raw.environment;
  const policyVersion = raw.consentPolicyVersion;

  return {
    // Exactly true, never "true" or 1. The kill switch is the one field where a coercion that
    // happens to be truthy would turn a deployment on that nobody meant to turn on.
    enabled: raw.enabled === true,
    gtmContainerId: text(raw.gtmContainerId),
    ga4MeasurementId: text(raw.ga4MeasurementId),
    centralGa4MeasurementId: text(raw.centralGa4MeasurementId),
    // Exactly true, for the same reason `enabled` is: this one decides whether a second property is
    // fed, and a coercion that happened to be truthy would start reporting a partner's visitors to
    // an account they were never told about.
    centralAnalyticsEnabled: raw.centralAnalyticsEnabled === true,
    googleAdsConversionId: text(raw.googleAdsConversionId),
    adsLabels: labelMap(raw.adsLabels),
    metaPixelId: text(raw.metaPixelId),
    tiktokPixelId: text(raw.tiktokPixelId),
    activeVendors: Array.isArray(raw.activeVendors)
      ? (raw.activeVendors.filter((vendor): vendor is TrackingVendor =>
          VENDORS.includes(vendor as TrackingVendor),
        ) as TrackingVendor[])
      : null,
    consentPolicyVersion: typeof policyVersion === "number" && Number.isFinite(policyVersion)
      ? policyVersion
      : null,
    privacyPolicyUrl: text(raw.privacyPolicyUrl),
    cookiePolicyUrl: text(raw.cookiePolicyUrl),
    // An unrecognised environment is left null on purpose: config.ts refuses to send anything
    // without a known one, and quietly substituting a guess here would defeat that.
    environment: typeof environment === "string" && ENVIRONMENTS.includes(environment as TrackingEnvironment)
      ? (environment as TrackingEnvironment)
      : null,
    allowedHostnames: textList(raw.allowedHostnames),
  };
}

// ── Vendor disclosure ────────────────────────────────────────────────────────

/**
 * Display names for the consent banner's disclosure. GA4 and Google Ads are one processor and are
 * named once — listing "Google" twice reads as two companies.
 */
const VENDOR_NAMES: Record<TrackingVendor, string> = {
  ga4: "Google",
  googleAds: "Google",
  meta: "Meta",
  tiktok: "TikTok",
};

/**
 * The processors this tenant actually feeds, for the banner to name. Derived from the configuration
 * rather than hard-coded, because naming a processor a tenant does not use makes the disclosure
 * false — and an empty list is the honest answer while the configuration has not arrived.
 *
 * <p>Reactive: reading it in JSX re-renders the disclosure when the tenant response lands.</p>
 */
export function activeVendorNames(): string[] {
  const names: string[] = [];
  for (const vendor of trackingConfig().activeVendors ?? []) {
    const name = VENDOR_NAMES[vendor];
    if (name !== undefined && !names.includes(name)) names.push(name);
  }
  return names;
}

// ── Start-up ─────────────────────────────────────────────────────────────────

/**
 * The teardown of the running layer, or null when it is not running. Module-level rather than
 * per-caller: a second <c>startTracking</c> from a hot reload or a remounted layout would otherwise
 * open a second consent subscription, and every event would be considered twice.
 */
let running: (() => void) | null = null;

/**
 * Wire the tracking layer to the app. Called once, from the layout's <c>onMount</c>.
 *
 * <p>Two things happen, in this order. The tenant's tracking section is mirrored into the tracking
 * configuration as the theme store settles — inside an effect, because that response is a network
 * round trip and arrives after this returns. Then the consent subscription is opened, which reads
 * the stored decision synchronously and, only if it grants a category, brings the container up.</p>
 *
 * <p>Gated on <c>themeState.loaded</c> so that "the server has answered" means what it says. Before
 * the answer the configuration is already <c>TRACKING_OFF</c>; pushing an empty one first would
 * merely mark it loaded while it is still unknown.</p>
 *
 * @returns the teardown. The app never calls it — the layer lives as long as the document — but a
 *   subscription with no way off the list is a leak by construction, and tests need it.
 */
export function startTracking(): () => void {
  if (running !== null) return running;

  // First thing, before the router has a chance to rewrite the address. A visitor arrives on
  // ?gclid=… and then spends twenty minutes configuring a door; by the time they ask for a quote the
  // URL that carried the campaign is long gone. Held in memory only until marketing is granted.
  captureAttribution();

  const disposeConfig = createRoot((dispose) => {
    createEffect(() => {
      if (!themeState.loaded) return;
      const section = (themeState.tenant as TenantConfig & TenantTrackingSection).tracking;
      setTrackingConfig(readTrackingConfig(section));
    });
    return dispose;
  });

  const stopAnalytics = initTracking();

  running = () => {
    stopAnalytics();
    disposeConfig();
    running = null;
  };
  return running;
}
